import { useEffect, useState } from 'react';
import { clsx } from 'clsx';
import { isEmpty } from 'lodash';
import { nanoid } from '@reduxjs/toolkit';
import { Controller, useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Button,
  Checkbox,
  Chip,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Typography,
} from '@material-ui/core';
import { Clear } from '@material-ui/icons';

import {
  NUTRITIONIST_TEST_INITIAL_STATE,
  setIsSearchingByText,
  setResetNutritionistTest,
} from 'features/nutritionists/state/nutritionist-test.slice';
import { useGetNutritionistsService } from 'features/nutritionists/services/get-nutritionists.service';
import { nutritionistsSelector } from 'features/nutritionists/selectors/nutritionists.selector';

import {
  NUTRITIONISTS_FILTERS,
  SERVICE_MODALITIES,
} from './nutritionist-search-filter.utils';
import { useStyles } from './nutritionist-search-filter.styles';
import { useHistory, useLocation } from 'react-router-dom';

const { modalityCriteria: _modalityCriteria, roleCriteria: _roleCriteria } =
  NUTRITIONIST_TEST_INITIAL_STATE;

export const NutritionistSearchFilter = () => {
  const [shouldShowCounter, setShouldShowCounter] = useState(false);

  const nutritionists = useSelector(nutritionistsSelector);

  const getNutritionistsService = useGetNutritionistsService();

  const history = useHistory();
  const location = useLocation();

  const form = useForm({
    defaultValues: {
      modalityCriteria: _modalityCriteria,
      roleCriteria: {
        roleName: _roleCriteria.roleName,
        roleSpecialty: _roleCriteria.roleSpecialty,
      },
    },
  });

  const dispatch = useDispatch();

  const [roleCriteria, modalityCriteria, roleSpecialty] = form.watch([
    'roleCriteria.roleName',
    'modalityCriteria',
    'roleCriteria.roleSpecialty',
  ]);

  const classes = useStyles();
  const getFormValuesFromSearch = search => {
    const searchParams = new URLSearchParams(search);
    const rawModes = searchParams.get('modes');
    const rawKeySpecialities = searchParams.get('keySpecialities');

    const modes = (rawModes || '')
      .split(',')
      .filter(Boolean);

    const modalityCriteriaFromSearch = rawModes
      ? {
          online: modes.includes('online'),
          onsite: modes.includes('onsite'),
          home: modes.includes('home'),
        }
      : _modalityCriteria;

    const keySpecialities = (rawKeySpecialities || '').split(',').filter(Boolean);

    let roleName = _roleCriteria.roleName;
    let roleSpecialty = _roleCriteria.roleSpecialty;

    for (const [currentRoleName, filter] of Object.entries(NUTRITIONISTS_FILTERS)) {
      const matchingOption = filter.options.find(option =>
        option.specialties.some(
          specialty =>
            specialty.type === 'keySpecialities' &&
            keySpecialities.includes(specialty.key),
        ),
      );

      if (matchingOption) {
        roleName = currentRoleName;
        roleSpecialty = matchingOption.value;
        break;
      }
    }

    return {
      hasFilters: modes.length > 0 || keySpecialities.length > 0,
      values: {
        modalityCriteria: modalityCriteriaFromSearch,
        roleCriteria: {
          roleName,
          roleSpecialty,
        },
      },
    };
  };

  // const getFormValuesFromSearch = search => {
  //   const searchParams = new URLSearchParams(search);
  //   const rawModes = searchParams.get('modes');
  //   const rawKeySpecialities = searchParams.get('keySpecialities');

  //   const modes = (rawModes || '')
  //     .split(',')
  //     .filter(Boolean);

  //   const modalityCriteriaFromSearch = rawModes
  //     ? {
  //         online: modes.includes('online'),
  //         onsite: modes.includes('onsite'),
  //         home: modes.includes('home'),
  //       }
  //     : _modalityCriteria;

  //   const keySpecialities = (rawKeySpecialities || '').split(',').filter(Boolean);

  //   let roleName = _roleCriteria.roleName;
  //   let roleSpecialty = _roleCriteria.roleSpecialty;

  //   for (const [currentRoleName, filter] of Object.entries(NUTRITIONISTS_FILTERS)) {
  //     const matchingOption = filter.options.find(option =>
  //       option.specialties.some(
  //         specialty =>
  //           specialty.type === 'keySpecialities' &&
  //           keySpecialities.includes(specialty.key),
  //       ),
  //     );

  //     if (matchingOption) {
  //       roleName = currentRoleName;
  //       roleSpecialty = matchingOption.value;
  //       break;
  //     }
  //   }

  //   return {
  //     hasFilters: modes.length > 0 || keySpecialities.length > 0,
  //     values: {
  //       modalityCriteria: modalityCriteriaFromSearch,
  //       roleCriteria: {
  //         roleName,
  //         roleSpecialty,
  //       },
  //     },
  //   };
  // };

  const onSubmitHandler = form.handleSubmit(data => {
    dispatch(setIsSearchingByText(true));

    const modalities = Object.keys(data.modalityCriteria).filter(
      key => data.modalityCriteria[key],
    );

    const {
      roleCriteria: { roleName, roleSpecialty },
    } = data;


    const option = NUTRITIONISTS_FILTERS[roleName].options.find(
      option => option.value === roleSpecialty,
    );

    const specialties = option?.specialties.filter(
      specialty => specialty.type === 'keySpecialities',
    ).map(specialty => specialty.key) ?? [];

    let queryParams = '?';

    if (modalities.length > 0) {
      queryParams = queryParams + `modes=${modalities.join(',')}`;
    }

    if (specialties.length > 0) {
      queryParams = queryParams + `&keySpecialities=${specialties.join(',')}`;
    }

    history.push({
      pathname: history.location.pathname,
      search: queryParams,
    })

  });

  const onResetHandler = () => {
    form.reset();
    setShouldShowCounter(false);
    dispatch(setResetNutritionistTest());
    dispatch(setIsSearchingByText(false));

    history.replace({
      pathname: history.location.pathname,
      search: '?page=1&limit=10',
    });
  };

  const isSubmitDisabled = Object.values(modalityCriteria).every(
    value => value === false,
  );

  useEffect(() => {
    const options = NUTRITIONISTS_FILTERS[roleCriteria].options;
    const [option] = options;
    const isCurrentSpecialtyValid = options.some(
      currentOption => currentOption.value === roleSpecialty,
    );

    if (option && !isCurrentSpecialtyValid) {
      form.setValue('roleCriteria.roleSpecialty', option.value);
    }
  }, [roleCriteria, roleSpecialty]);

  useEffect(() => {
    const { hasFilters, values } = getFormValuesFromSearch(location.search);
    form.reset(values);
    dispatch(setIsSearchingByText(hasFilters));
  }, [location.search]);

  return (
    <form onSubmit={onSubmitHandler}>
      <Grid container spacing={4}>
        <Grid item xs={12} sm={6}>
          <Typography variant="h6" className={classes.sectionTitle}>
            Encuentra a tu nutriólogo por
          </Typography>
          <Controller
            control={form.control}
            name="roleCriteria.roleName"
            render={({ field }) => (
              <FormControl fullWidth>
                <InputLabel>¿Qué objetivo tienes en mente?</InputLabel>
                <Select
                  label="¿Qué objetivo tienes en mente?"
                  value={field.value}
                  onChange={field.onChange}
                >
                  {Object.keys(NUTRITIONISTS_FILTERS).map(key => (
                    <MenuItem key={nanoid()} value={key}>
                      {NUTRITIONISTS_FILTERS[key]?.name ?? ''}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <Typography variant="h6" className={classes.sectionTitle}>
            Tipo de consulta
          </Typography>
          {SERVICE_MODALITIES.map(modality => (
            <Controller
              key={nanoid()}
              control={form.control}
              name={`modalityCriteria.${modality.value}`}
              render={({ field }) => (
                <Checkbox
                  checked={field.value}
                  onChange={field.onChange}
                  icon={<Chip label={modality.name} />}
                  checkedIcon={
                    <Chip
                      label={modality.name}
                      className={classes.chipChecked}
                    />
                  }
                />
              )}
            />
          ))}
        </Grid>
        <Grid item xs={12} sm={6}>
          {!isEmpty(NUTRITIONISTS_FILTERS[roleCriteria].options) && (
            <Controller
              control={form.control}
              name="roleCriteria.roleSpecialty"
              render={({ field }) => (
                <FormControl fullWidth>
                  <InputLabel>Elije en que te quieres enfocar</InputLabel>
                  <Select
                    label="Elije en que te quieres enfocar"
                    value={field.value}
                    onChange={field.onChange}
                  >
                    {NUTRITIONISTS_FILTERS[roleCriteria].options.map(option => (
                      <MenuItem key={nanoid()} value={option.value}>
                        {option.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
            />
          )}
        </Grid>
        <Grid item xs={12} sm={6}>
          <Button
            type="submit"
            variant="contained"
            color="primary"
            disabled={getNutritionistsService.isLoading || isSubmitDisabled}
          >
            {getNutritionistsService.isLoading
              ? 'Buscando...'
              : 'Filtrar resultados'}
          </Button>
        </Grid>
      </Grid>
      {shouldShowCounter && (
        <Box mt={4} className={clsx(classes.group, classes.bordered)}>
          <Typography variant="h6" className={classes.resultsTitle}>
            {nutritionists.length} resultados encontrados
          </Typography>
          <Button
            size="small"
            variant="text"
            color="primary"
            startIcon={<Clear />}
            onClick={onResetHandler}
          >
            Limpiar filtros
          </Button>
        </Box>
      )}
    </form>
  );
};
